import { useEffect, useState } from 'react';
import type { StockLocationDto, StockLocationKind } from '@ecom/contracts';
import { ToggleField } from '@/components/fields';
import { FilterBar } from '@/components/filters';
import { ArchiveIcon, PlusIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Sheet, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import { Alert, Button, Card, ConfirmDialog, EmptyState, Field, Input, Pill, Select, TableSkeleton, cx } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { SettingsPage } from '@/pages/settings';
import { useAuthStore } from '@/stores/auth.store';
import { useShippingStore } from '@/stores/shipping.store';
import { useStockLocationsStore } from '@/stores/stock-locations.store';

const KINDS: StockLocationKind[] = ['WAREHOUSE', 'BRANCH'];

/**
 * Warehouses and branches (ADR-089): where stock is kept, the shipping zones
 * each serves first, and what each holds. The main location holds whatever no
 * other count says; a pickup method names the branch it is collected from.
 */
export function StockLocationsPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const user = useAuthStore((state) => state.user);
  const { items, nextCursor, total, search, loading, error, load, loadMore } = useStockLocationsStore();
  const { zones, load: loadZones } = useShippingStore();
  const [editing, setEditing] = useState<StockLocationDto | 'new' | null>(null);
  const owner = user?.role === 'OWNER';

  useEffect(() => {
    if (!owner) return;
    void load();
    // The zones a location covers are named from the shipping page's list.
    void loadZones();
  }, [owner, load, loadZones]);

  // The API enforces this; hiding the form is a courtesy, not the boundary.
  if (!owner) {
    return (
      <SettingsPage title="settingsLocations" subtitle="settingsLocationsHint">
        <Card className="flex flex-1 flex-col">
          <EmptyState title={t('ownerOnlySettings')} icon={<ArchiveIcon />} />
        </Card>
      </SettingsPage>
    );
  }

  const zoneName = (id: string) => {
    const zone = zones.find((entry) => entry.id === id);
    return zone ? zone.translations?.[locale] || zone.name : '—';
  };
  const add = (
    <Button onClick={() => setEditing('new')}>
      <PlusIcon className="size-4" />
      {t('locationAdd')}
    </Button>
  );

  return (
    <SettingsPage title="settingsLocations" subtitle="settingsLocationsHint" actions={add} fill>
      <ListCard>
        <ListToolbar>
          <FilterBar search={search} onSearch={(term) => void load({ search: term })} placeholder={t('locationsSearch')} total={total ?? undefined} shown={items.length} />
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <TableSkeleton columns={4} />
        ) : items.length === 0 ? (
          <EmptyState title={t('locationsEmpty')} icon={<ArchiveIcon />} action={add} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('location')}</Th>
                  <Th>{t('locationCovers')}</Th>
                  <Th>{t('locationUnits')}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {items.map((location) => (
                  <Tr key={location.id} onClick={() => setEditing(location)} className={cx(!location.isActive && 'opacity-60')}>
                    <Td>
                      <span className="block font-medium text-ink">{location.name[locale] || location.name.en}</span>
                      <span className="block text-xs text-ink-muted">
                        {t(`locationKind${location.kind}`)}
                        {location.address ? ` · ${location.address}` : ''}
                      </span>
                    </Td>
                    <Td className="max-w-80 text-sm text-ink-muted">
                      {location.zoneIds.length === 0 ? t('locationEverywhere') : location.zoneIds.map(zoneName).join('، ')}
                    </Td>
                    <Td data-numeric>{count(location.units, locale)}</Td>
                    <Td align="end">
                      {location.isDefault ? <Pill tone="info">{t('locationMain')}</Pill> : null}
                      {!location.isActive ? <Pill>{t('locationInactive')}</Pill> : null}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>

      {editing ? <EditSheet location={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </SettingsPage>
  );
}

function EditSheet({ location, onClose }: { location: StockLocationDto | null; onClose: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { save, remove, saving, saveError, clearSaveError } = useStockLocationsStore();
  const zones = useShippingStore((state) => state.zones);
  const [nameEn, setNameEn] = useState(location?.name.en ?? '');
  const [nameAr, setNameAr] = useState(location?.name.ar ?? '');
  const [kind, setKind] = useState<StockLocationKind>(location?.kind ?? 'BRANCH');
  const [address, setAddress] = useState(location?.address ?? '');
  const [zoneIds, setZoneIds] = useState<string[]>(location?.zoneIds ?? []);
  const [sortOrder, setSortOrder] = useState(String(location?.sortOrder ?? 1));
  const [isActive, setIsActive] = useState(location?.isActive ?? true);
  const [deleting, setDeleting] = useState(false);
  const order = /^\d+$/.test(sortOrder.trim()) ? Number(sortOrder) : null;
  const ready = Boolean(nameEn.trim() || nameAr.trim()) && order !== null;

  useEffect(() => {
    clearSaveError();
  }, [clearSaveError]);

  async function submit() {
    if (!ready || order === null) return;
    const input = {
      name: { en: nameEn.trim(), ar: nameAr.trim() },
      kind,
      address: address.trim() || null,
      zoneIds,
      sortOrder: order,
      isActive,
    };
    if (await save(location?.id ?? null, input)) onClose();
  }

  return (
    <>
      <Sheet
        open
        size="lg"
        title={location ? location.name[locale] || location.name.en : t('locationAdd')}
        subtitle={location?.isDefault ? t('locationMainHint') : t('locationEditHint')}
        onClose={() => {
          if (!deleting) onClose();
        }}
        footer={
          <>
            {location && !location.isDefault ? (
              <Button variant="secondary" className="me-auto text-danger" onClick={() => setDeleting(true)} disabled={saving}>
                {t('delete')}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              {t('cancel')}
            </Button>
            <Button disabled={!ready || saving} onClick={() => void submit()}>
              {t('save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('nameArabic')} htmlFor="location-name-ar">
              <Input id="location-name-ar" dir="rtl" maxLength={80} value={nameAr} onChange={(event) => setNameAr(event.target.value)} />
            </Field>
            <Field label={t('nameEnglish')} htmlFor="location-name-en">
              <Input id="location-name-en" dir="ltr" maxLength={80} value={nameEn} onChange={(event) => setNameEn(event.target.value)} />
            </Field>
            <Field label={t('locationKind')} htmlFor="location-kind">
              <Select id="location-kind" className="w-full" value={kind} onChange={(event) => setKind(event.target.value as StockLocationKind)}>
                {KINDS.map((option) => (
                  <option key={option} value={option}>
                    {t(`locationKind${option}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('locationOrder')} htmlFor="location-order" hint={t('locationOrderHint')}>
              <Input id="location-order" dir="ltr" inputMode="numeric" value={sortOrder} invalid={order === null} onChange={(event) => setSortOrder(event.target.value)} />
            </Field>
          </div>
          <Field label={t('locationAddress')} htmlFor="location-address">
            <Input id="location-address" maxLength={300} value={address} onChange={(event) => setAddress(event.target.value)} />
          </Field>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium text-ink">{t('locationCovers')}</legend>
            <p className="mb-1 text-xs text-ink-muted">{t('locationCoversHint')}</p>
            {zones.map((zone) => (
              <label key={zone.id} className="flex cursor-pointer items-center gap-3 rounded-control border border-line px-3 py-2 hover:bg-surface-sunken">
                <input
                  type="checkbox"
                  className="size-4 accent-brand-600"
                  checked={zoneIds.includes(zone.id)}
                  onChange={(event) =>
                    setZoneIds((current) => (event.target.checked ? [...current, zone.id] : current.filter((id) => id !== zone.id)))
                  }
                />
                <span className="text-sm text-ink">{zone.translations?.[locale] || zone.name}</span>
              </label>
            ))}
          </fieldset>

          {location?.isDefault ? null : (
            <ToggleField label={t('locationActive')} hint={t('locationActiveHint')} checked={isActive} onChange={setIsActive} />
          )}
          {saveError ? <Alert>{saveError}</Alert> : null}
        </div>
      </Sheet>
      <ConfirmDialog
        open={deleting}
        title={t('locationDeleteTitle')}
        body={t('locationDeleteBody')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (location && (await remove(location.id))) onClose();
          setDeleting(false);
        }}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}
